import { useEffect, useState } from "react";
import { Link, Route, Switch, useLocation } from "react-router-dom";
import { CheckCircleIcon, DotsHorizontalIcon, ExclamationCircleIcon } from "@heroicons/react/outline";
import { useTranslation } from "react-i18next";
import AtlasKnowledgeHub from "./AtlasKnowledgeHub";
import AtlasAssetLibrary from "./AtlasAssetLibrary";
import { AtlasKnowledge } from "../interfaces";
import { getData, patchData } from "../backend";
import useSWR from "swr";

function AtlasTab(props: { label: string, link: string, isSelected: boolean }) {
  return (
    <Link to={props.link}>
      <div className={`font-gilroy font-semibold text-sm pb-3 -mb-px border-b-2 transition-colors ${props.isSelected ? "text-blue border-blue" : "text-gray-400 border-transparent hover:text-gray-900"}`}>
        {props.label}
      </div>
    </Link>
  );
}

function Atlas() {
  const { t } = useTranslation();
  const pathname = useLocation().pathname;

  const { data: knowledge, mutate } = useSWR<AtlasKnowledge>("/atlas/knowledge/", getData, {
    revalidateOnFocus: false
  });

  async function changeKnowledge(updatedKnowledge: AtlasKnowledge) {
    setSaveState("HAS_CHANGES");
    await mutate(updatedKnowledge, { revalidate: false });
  }

  async function saveKnowledge() {
    setSaveState("SAVING");

    await patchData("/atlas/knowledge/", knowledge)
      .then(mutate)
      .then(() => setSaveState("SAVED"))
      .catch(() => setSaveState("ERROR"));
  }

  const [saveState, setSaveState] = useState<"INITIAL" | "HAS_CHANGES" | "SAVING" | "SAVED" | "ERROR">("INITIAL");

  useEffect(() => {
    document.title = `${t("Atlas")} | Portant`;
    return (() => { document.title = "Portant" });
  }, [t]);

  // Passive sync indicator — live sync replaces the explicit Save button.
  let syncIndicator = null;
  if (saveState === "HAS_CHANGES") {
    syncIndicator = (
      <div className="flex items-center text-gray-400">
        <button className="btn btn-blue text-sm w-20 py-1" onClick={saveKnowledge}>Save</button>
      </div>
    );
  }
  else if (saveState === "SAVING") {
    syncIndicator = (
      <div className="flex items-center text-gray-400">
        <DotsHorizontalIcon className="h-5 w-5 mr-1" />
        <div className="text-sm font-gilroy font-semibold">{t("Saving")}</div>
      </div>
    );
  }
  else if (saveState === "SAVED") {
    syncIndicator = (
      <div className="flex items-center text-green">
        <CheckCircleIcon className="h-5 w-5 mr-1" />
        <div className="text-sm font-gilroy font-semibold">{t("Saved")}</div>
      </div>
    );
  }
  else if (saveState === "ERROR") {
    syncIndicator = (
      <div className="flex items-center text-red">
        <ExclamationCircleIcon className="h-5 w-5 mr-1" />
        <div className="text-sm font-gilroy font-semibold">{t("Error")}</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col border border-gray-200 rounded-lg bg-white w-full h-full min-h-0 pb-4">

      {/* Header — pinned inside the canvas so the sync state stays visible */}
      <div className="flex items-start justify-between px-8 pt-8 flex-shrink-0">
        <div className="flex flex-col">
          <div className="font-gilroy font-semibold text-2xl text-gray-900">
            Portant Sales Agent
          </div>
          <div className="text-sm text-gray-400 font-semibold mt-1">
            {t("The organisational knowledge behind your sales agent — what you sell, who you sell to, and how you talk.")}
          </div>
        </div>
        <div className="flex items-center h-8 flex-shrink-0 ml-6">
          {syncIndicator}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-8 border-b border-gray-200 px-8 mt-6 flex-shrink-0">
        <AtlasTab label={t("Knowledge Hub")} link="/sales-agent" isSelected={pathname === "/sales-agent"} />
        <AtlasTab label={t("Asset Library")} link="/sales-agent/assets" isSelected={pathname === "/sales-agent/assets"} />
      </div>

      {/* Content — scrolls beneath the pinned header and tabs */}
      <div className="flex flex-col flex-grow min-h-0 overflow-y-auto px-8">
        <Switch>
          <Route path="/sales-agent/assets">
            <AtlasAssetLibrary />
          </Route>
          <Route path="/sales-agent">
            {!!knowledge
              ? <AtlasKnowledgeHub knowledge={knowledge} onChange={changeKnowledge} />
              : <img src="https://static.portant.co/portant-loading-gray.svg" alt="Loading Logo" className='mx-auto my-24 w-20 h-20' />
            }
          </Route>
        </Switch>
      </div>
    </div>
  );
}

export default Atlas;
